// expectedCanisterHashes.ts — Expected WASM module hashes per canister.
//
// Update this file after every successful production deploy.
// Values come from the CI build artifact: `sha256sum *.wasm.gz` output.
// Jerry pastes the expected hash into the Canister Inspector "Verify hash" input,
// and the UI highlights match (gold) vs mismatch (attention/red) per canister.
//
// Format: lowercase hex string (64 chars, SHA-256 of the compressed WASM).
// Leave as empty string '' until the first mainnet deploy.

export interface ExpectedHash {
  // Human label matching CanisterRollup.label
  label: string;
  // Expected SHA-256 hex string of the gzip-compressed WASM module.
  // Empty string means "not yet deployed to mainnet — skip verification."
  expectedHash: string;
  // ISO date string of when this expected hash was recorded.
  recordedAt: string;
}

// Keyed by canister label (must match CanisterRollup.label from adminAgent.ts)
export const EXPECTED_HASHES: Record<string, ExpectedHash> = {
  'auth_canister': {
    label: 'auth_canister',
    expectedHash: '',
    recordedAt: '',
  },
  'lobby_canister': {
    label: 'lobby_canister',
    expectedHash: '',
    recordedAt: '',
  },
  'game_canister': {
    label: 'game_canister',
    expectedHash: '',
    recordedAt: '',
  },
  'subscription_canister': {
    label: 'subscription_canister',
    expectedHash: '',
    recordedAt: '',
  },
  'dominus_admin': {
    label: 'dominus_admin',
    expectedHash: '',
    recordedAt: '',
  },
};

/**
 * Look up the expected hash for a given canister label.
 * Returns null if the label is unknown or no hash has been recorded yet.
 */
export function getExpectedHash(label: string): ExpectedHash | null {
  return EXPECTED_HASHES[label] ?? null;
}

/**
 * Compare a live wasm_module_hash string against the expected hash.
 * Returns 'match' | 'mismatch' | 'unknown' (when no expected hash recorded).
 */
export function verifyHash(
  label: string,
  liveHash: string,
): 'match' | 'mismatch' | 'unknown' {
  const expected = getExpectedHash(label);
  if (!expected || expected.expectedHash === '') return 'unknown';
  return liveHash.toLowerCase() === expected.expectedHash.toLowerCase()
    ? 'match'
    : 'mismatch';
}
